import { useEffect, useRef, useState } from 'react';
import permissions from '@ion/api/Apollo/permissions';
import { useValidateIntegration, useValidationCluster } from '@ion/api';
import preventUpload from './prevent-upload';
import buildValidatePayload from './buildValidatePayload';

const GENERIC_VALIDATION_ERROR = 'There was an error validating the file. Please try again.';

// Validates the uploaded file whenever it or its resolved type changes. The
// auto-detected type and a user override share this single path, and Apply re-locks
// (validated → false) until the new type validates. The permission is keyed on the
// resolved type so the override runs under the right one.
export default function useUploadValidation({ upload, playbookState, integration, uploadState, allowedTypes = [] }) {
  const [validated, setValidated] = useState(false);
  const { clusterId } = useValidationCluster();
  // Each validation run gets an id. A response — success OR failure — is only
  // honored if it's still the latest request, so a slow earlier validation can't
  // re-enable or re-lock Apply for a type the user has since overridden away from.
  const requestRef = useRef(0);
  // onError hands us the mapped, user-friendly message (mapErrors isn't exported).
  // useMrMutation always passes an onError to Apollo, so a failed validation RESOLVES
  // (Apollo doesn't reject when onError is set) — we read this message in the resolve
  // handler below rather than a (never-reached) catch.
  const errorMsgRef = useRef(null);

  const uploadType = upload?.type ?? null;
  const permission = permissions[uploadState.uploadPermissionOptions[uploadType]];

  const [validate] = useValidateIntegration({
    onError: ({ errorMsg }) => {
      errorMsgRef.current = errorMsg;
    },
    permission,
  });

  useEffect(() => {
    setValidated(false);
    if (!upload?.result || !upload?.type) {
      return;
    }
    // Detection resolved a type the user can't upload (they hold only the other
    // permission). Don't fire a doomed validation that just returns permission-denied;
    // tell them plainly. They can still override to a type they're allowed to upload.
    if (!allowedTypes.includes(upload.type)) {
      uploadState.setError(`This looks like a ${upload.type} file, which you don't have permission to upload.`);
      return;
    }
    const msg = preventUpload(upload.type, upload.extension, upload.result, playbookState, clusterId);
    if (msg) {
      uploadState.setError(msg);
      return;
    }
    uploadState.setError(null);

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    errorMsgRef.current = null;
    Promise.resolve(
      validate(
        buildValidatePayload({ result: upload.result, uploadType: upload.type, playbookState, integration, clusterId })
      )
    )
      .then(response => {
        // Ignore a superseded request (type overridden mid-flight) so a stale
        // response can't validate — or re-lock — the wrong type.
        if (requestId !== requestRef.current) {
          return;
        }
        if (response?.integration) {
          uploadState.setError(null);
          setValidated(true);
          return;
        }
        // A failed validation resolves here with no integration (see errorMsgRef note);
        // surface the message onError stashed for this request.
        setValidated(false);
        uploadState.setError(errorMsgRef.current ?? GENERIC_VALIDATION_ERROR);
      })
      .catch(() => {
        // Defensive only — Apollo resolves handled errors above, so this catches just
        // an unexpected throw. Same staleness guard as the resolve path.
        if (requestId === requestRef.current) {
          setValidated(false);
          uploadState.setError(errorMsgRef.current ?? GENERIC_VALIDATION_ERROR);
        }
      });
    // Re-validate when the file/type changes or when the validation cluster resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload, clusterId]);

  return { validated };
}
